import Big from 'big.js';
import { Issuer } from 'smg/cc';
import * as models from 'smg/customer-api/models';

import i18n from '../../i18n';
import { State } from '../state';
import { CardState, Payment, Split, SplitEntry, SplitEntryStatus } from '../types';

export interface PlanEntry {
    id: string;
    amount: Big;
    first6: string;
    last4: string;
    brand: string;
    payment: Payment;
    status: SplitEntryStatus | 'single';
    splitEntry?: SplitEntry;
}

export interface PaymentPlanView {
    split: boolean;
    entries: PlanEntry[];
    cover: Big;
    paid: Big;
    left: Big;
    assigned: Big;
    over: Big;
    balanced: boolean;
    ready: boolean;
}

export function CardBrand(first6: string): string {
    return Issuer(first6 || '');
}

// ApplyCardIssuer writes the brand name and shows the matching card icon.
export function ApplyCardIssuer(root: ParentNode, first6: string): void {
    const label = CardBrand(first6);
    const issuer = root.querySelector('.cc-issuer');
    if (!label || !issuer) {
        return;
    }
    issuer.textContent = label;
    // The card art covers the brands the checkout takes; any other issuer keeps the generic icon.
    const icon = root.querySelector(`.cc-icon-${label.toLowerCase()}`);
    if (!icon) {
        return;
    }
    root.querySelector('.cc-icon-generic')?.classList.add('d-none');
    icon.classList.remove('d-none');
}

// ChargedPayment is the card a row will charge. A sameAs copy uses the source row's card.
// A paid row holds money, and a row whose charge got no answer must retry with its key, so the split cannot drop either.
export function SplitCommitted(split: Split): boolean {
    return split.entries.some(entry => entry.status === 'paid' || entry.status === 'unknown');
}

// SplitSettled is true when every row is paid: nothing is left to charge, and only finishing the order remains.
export function SplitSettled(split: Split): boolean {
    return split.entries.length > 0 && split.entries.every(entry => entry.status === 'paid');
}

// SubmitRunning is true from Place Order until that run ends: no second submit starts, and the order cannot be started over.
export function SubmitRunning(state: State): boolean {
    return state.phase === 'preparing' || state.phase === 'charging' || state.phase === 'recovering' || state.phase === 'finishing';
}

// SplitCharging is true while a split's cards are being charged or the order is finishing: its rows and controls are locked.
export function SplitCharging(state: State): boolean {
    return Boolean(state.split) && (state.phase === 'charging' || state.phase === 'finishing');
}

// OrderPaid is true once every charge on this order went through: the card was paid, or every split row is.
// The cart is deleted from here on, and the totals no longer change.
export function OrderPaid(state: State): boolean {
    return Boolean(state.payment.success) || Boolean(state.split && SplitSettled(state.split));
}

// PaymentCommitted is true once money may have moved on this order: a charge is running, a single card was paid or
// got no answer (its key is held for the retry), or a split row is paid or unanswered. Starting over would abandon it.
export function PaymentCommitted(state: State): boolean {
    if (SubmitRunning(state) || state.payment.success || state.orderPaymentIdemKey) {
        return true;
    }
    return Boolean(state.split && SplitCommitted(state.split));
}

// EntryCardState is the one place that decides a row's CardState.
export function EntryCardState(entry: SplitEntry): CardState {
    if (entry.sameAs) {
        return 'reuse';
    }
    if (entry.payment.pendingPaymentOptionCreate) {
        return 'staged';
    }
    if (entry.source === 'saved') {
        return 'saved';
    }
    return 'draft';
}

export function ChargedPayment(entries: SplitEntry[], entry: SplitEntry): Payment {
    if (entry.sameAs) {
        const source = entries.find(item => item.id === entry.sameAs);
        if (source) {
            return source.payment;
        }
    }
    return entry.payment;
}

export function EntryCard(payment: Payment): { first6: string; last4: string; brand: string } {
    const first6 = String(payment.paymentOption?.creditCardFirst6 || payment.pendingPaymentOptionCreate?.creditCardFirst6 || '');
    const last4 = String(payment.paymentOption?.creditCardLast4 || payment.pendingPaymentOptionCreate?.creditCardLast4 || '');
    const brand = CardBrand(first6) || (last4 ? 'Credit' : i18n('checkoutSplitNewCard'));
    return { first6, last4, brand };
}

export function EntryLabel(payment: Payment): string {
    const card = EntryCard(payment);
    if (!card.last4) {
        return card.brand;
    }
    return i18n('checkoutSplitCardLine', { brand: card.brand, last4: card.last4, amount: '' }).replace(/,\s*$/, '');
}

// PaymentPlan is the one answer for what this checkout will charge.
// A one-method payment is a plan of one entry for the whole amount.
export function PaymentPlan(state: State): PaymentPlanView {
    const cover = state.totalAmount;
    if (!state.split) {
        const payment = state.payment;
        const card = EntryCard(payment);
        const paypal = payment.paymentOptionTypeID === models.PaymentOptionTypeID.PAYMENT_OPTION_TYPE_PAY_PAL;
        const hasMethod = paypal || Boolean(payment.paymentOption?.id || payment.pendingPaymentOptionCreate || payment.paymentOptionTypeID);
        const entry: PlanEntry = {
            id: 'single',
            amount: cover,
            first6: card.first6,
            last4: card.last4,
            brand: card.brand,
            payment,
            status: 'single',
        };
        return {
            split: false,
            entries: hasMethod ? [entry] : [],
            cover,
            paid: Big(0),
            left: cover,
            assigned: hasMethod ? cover : Big(0),
            over: Big(0),
            balanced: true,
            ready: paypal || Boolean(payment.paymentOption?.id || payment.pendingPaymentOptionCreate),
        };
    }

    let assigned = Big(0);
    let paid = Big(0);
    const entries: PlanEntry[] = state.split.entries.map(entry => {
        assigned = assigned.plus(entry.amount);
        if (entry.status === 'paid') {
            paid = paid.plus(entry.amount);
        }
        const payment = ChargedPayment(state.split?.entries ?? [], entry);
        const card = EntryCard(payment);
        return {
            id: entry.id,
            amount: entry.amount,
            first6: card.first6,
            last4: card.last4,
            brand: card.brand,
            payment,
            status: entry.status,
            splitEntry: entry,
        };
    });
    const over = assigned.minus(cover);
    const due = cover.minus(paid);
    const balanced = assigned.eq(cover);
    const ready = balanced && entries.every(entry => {
        if (entry.status === 'paid') {
            return true;
        }
        if (!entry.amount.gt(0)) {
            return false;
        }
        return Boolean(entry.payment.paymentOption?.id || entry.payment.pendingPaymentOptionCreate);
    });
    return {
        split: true,
        entries,
        cover,
        paid,
        left: due.gt(0) ? due : Big(0),
        assigned,
        over: over.gt(0) ? over : Big(0),
        balanced,
        ready,
    };
}

export function Unassigned(plan: PaymentPlanView): Big {
    const gap = plan.cover.minus(plan.assigned);
    return gap.gt(0) ? gap : Big(0);
}
